import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';

import Menu, { MenuItem } from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconButton from 'components/common/IconButton';
import IconTransmit from 'components/icons/IconTransmit';
import IconMove from 'components/icons/IconMove';
import IconTriangleBottom from 'components/icons/IconTriangleBottom';
import IconPrint from 'components/icons/IconPrint';
import IconTrashCan from 'components/icons/IconTrashCan';
import IconArticle from 'components/icons/IconArticle';
import IconCrossBold from 'components/icons/IconCrossBold';
import IconUndo from 'components/icons/IconUndo';
import Progress from 'components/common/Progress';
import Snackbar from 'components/common/Snackbar';
import Button from 'components/common/Button';

import formatSinglePlural from 'utils/formatSinglePlural';
import formatOrderNumber from 'utils/formatOrderNumber';

import { transmitOrderLines, updateOrderLines } from 'redux/modules/ordersLines';
import { addNotification } from 'redux/modules/notifications';

import OrdersActionsBarMoveForm from './OrdersActionsBarMoveForm';

const styles = theme => ({
  root: {
    padding: '12px 0',
    justifyContent: 'center',
  },
  button: {
    margin: '0 10px',
    color: theme.palette.grey[600],
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      color: theme.palette.secondary[650],
    },
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px',
    },
  },
  buttonMove: {
    margin: '0 15px 0 20px',
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px',
    },
  },
  buttonSyncActive: {
    color: theme.palette.primary[500],

    '&:hover': {
      color: theme.palette.primary[700],
    },
  },
  iconSync: {
    width: 24,
    height: 26,
  },
  iconTransmit: {
    width: 20,
    height: 20,
    color: theme.palette.primary[500],
  },
  iconMove: {
    width: 24,
    height: 20,
    color: theme.palette.primary[500],
  },
  iconTriangleBottom: {
    width: 10,
    height: 5,
    marginLeft: 15,
  },
  iconPrint: {
    width: 22,
    height: 20,
    color: theme.palette.primary[500],
  },
  iconTrashCan: {
    width: 14,
    height: 18,
    color: theme.palette.primary[500],
  },
  iconSynchronization: {
    width: 26,
    height: 20,
  },
  iconRejected: {
    position: 'relative',
  },
  iconArticle: {
    display: 'block',
    width: 26,
    height: 26,
    color: theme.palette.grey[600],
  },
  iconCrossBold: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 12,
    height: 12,
    color: theme.palette.validation[500],
  },
  iconCrossBoldMove: {
    position: 'absolute',
    right: 0,
    top: 0,
    width: 12,
    height: 12,
    color: theme.palette.validation[500],
  },
  iconRedo: {
    position: 'absolute',
    right: 4,
    top: -12,
    width: 20,
    height: 20,
    color: theme.palette.validation[500],
    transform: 'scaleX(-1)',
  },
  iconUndo: {
    position: 'absolute',
    right: 3,
    top: 18,
    width: 20,
    height: 20,
    color: theme.palette.primary[500],
    transform: 'scaleY(-1)',
  },
  menuItem: {
    paddingTop: 8,
    paddingBottom: 8,
    minWidth: 170,
    textAlign: 'left',
    borderTop: 'none',
    '&:last-child': {
      marginBottom: 5,
    },
  },
  loaderContainer: {
    position: 'relative',
  },
});

class OrdersActionsBarPrimaryControls extends Component {
  constructor(props) {
    super(props);

    this.state = {
      anchorEl: null,
      moveMenuOpen: false,
      deleteMenuOpen: false,
      printMenuOpen: false,
      moveSection: null,
      transmitting: false,
      moving: false,
      extracting: false,
      snackbarOpen: false,
      snackbarMessage: '',
      transmittableOrderLines: [],
      transmitMenuOpen: false,
      withAttachment: false,
      withClaim: true,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onMenuExited = this.onMenuExited.bind(this);
    this.onMoveButtonClick = this.onMoveButtonClick.bind(this);
    this.onMoveMenuItemClick = this.onMoveMenuItemClick.bind(this);
    this.onRemoveButtonClick = this.onRemoveButtonClick.bind(this);
    this.onRemoveCancel = this.onRemoveCancel.bind(this);
    this.onRemoveSubmit = this.onRemoveSubmit.bind(this);
    this.onPrintButtonClick = this.onPrintButtonClick.bind(this);
    this.onPrintMenuItemClick = this.onPrintMenuItemClick.bind(this);
    this.onTransmitButtonClick = this.onTransmitButtonClick.bind(this);
    this.onRejectButtonClick = this.onRejectButtonClick.bind(this);
    this.onUndoRejectedButtonClick = this.onUndoRejectedButtonClick.bind(this);
    this.onSnackbarClose = this.onSnackbarClose.bind(this);
    this.onTransmitSubmitButtonClick = this.onTransmitSubmitButtonClick.bind(this);
    this.onTransmit = this.onTransmit.bind(this);
    this.onTransmitMenuClose = this.onTransmitMenuClose.bind(this);
  }

  onMenuClose() {
    this.setState({
      anchorEl: null,
      moveMenuOpen: false,
      deleteMenuOpen: false,
      printMenuOpen: false,
    });
  }

  onMenuExited() {
    this.setState({ moveSection: null });
  }

  onMoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      moveMenuOpen: true,
    });
  }

  onMoveMenuItemClick(value) {
    this.setState({ moveSection: value });
  }

  onRemoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
      deleteMenuOpen: true,
    });
  }

  onRemoveCancel() {
    this.setState({
      anchorEl: null,
      deleteMenuOpen: false,
    });
  }

  onRemoveSubmit() {
    this.setState({
      anchorEl: null,
      deleteMenuOpen: false,
    });
    this.props.onRemove();
  }

  onPrintButtonClick(event) {
    const { onPrint, orders } = this.props;
    if (orders) {
      this.setState({
        anchorEl: event.currentTarget,
        printMenuOpen: true,
      });
    } else {
      onPrint();
    }
  }

  onPrintMenuItemClick(value) {
    this.setState({
      anchorEl: null,
      printMenuOpen: false,
    });
    this.props.onPrint(value);
  }

  onTransmitButtonClick(event) {
    const { selectedOrderLines, actions } = this.props;
    const {
      transmittedOrderLines,
      electronicallyOrderLines,
      nonElectronicallyOrderLines,
    } = selectedOrderLines.reduce((accumulator, currentValue) => {
      if (currentValue.Claim
        && currentValue.Claim.length > 0
        && currentValue.Claim.filter(item => item['Claim Status']).length > 0) {
        return {
          ...accumulator,
          transmittedOrderLines: [
            ...accumulator.transmittedOrderLines,
            currentValue,
          ],
        };
      } else if (currentValue['Transmission Method'] === 'Electronically') {
        return {
          ...accumulator,
          electronicallyOrderLines: [
            ...accumulator.electronicallyOrderLines,
            currentValue,
          ],
        };
      }
      return {
        ...accumulator,
        nonElectronicallyOrderLines: [
          ...accumulator.nonElectronicallyOrderLines,
          currentValue,
        ],
      };
    }, {
      transmittedOrderLines: [],
      electronicallyOrderLines: [],
      nonElectronicallyOrderLines: [],
    });
    const errors = [];
    const warnings = [];
    const transmittableOrderLines = [
      ...transmittedOrderLines,
      ...electronicallyOrderLines,
    ];
    if (electronicallyOrderLines.length > 0 && transmittedOrderLines.length === 0) {
      if (transmittableOrderLines.filter(item => item.CMN && item.CMN.ID).length > 0) {
        this.setState({
          transmitMenuOpen: true,
          anchorEl: event.currentTarget,
          transmittableOrderLines,
        });
      } else {
        this.onTransmit({
          orderLines: electronicallyOrderLines,
        });
      }
    }
    if (transmittedOrderLines.length > 0) {
      warnings.push(`Please, note that order ${formatSinglePlural({
        quantity: transmittedOrderLines.length, single: 'line', plural: 'lines',
      })} ${transmittedOrderLines.map(item =>
        item['HCPC S Code']['HCPC S Code']).join(', ')} ${formatSinglePlural({
        quantity: transmittedOrderLines.length, single: 'was', plural: 'were',
      })} already transmitted before, proceed anyway?`);
    }
    if (nonElectronicallyOrderLines.length > 0) {
      errors.push(`Order ${formatSinglePlural({
        quantity: nonElectronicallyOrderLines.length, single: 'line', plural: 'lines',
      })} ${nonElectronicallyOrderLines.map(item =>
        item['HCPC S Code']['HCPC S Code']).join(', ')} "Transmission Method" is not set to "Electronically",  ${formatSinglePlural({
        quantity: nonElectronicallyOrderLines.length, single: 'it', plural: 'they',
      })} can't be transmitted`);
    }
    if (warnings.length > 0) {
      if (transmittableOrderLines.filter(item => item.CMN && item.CMN.ID).length > 0) {
        this.setState({
          transmitMenuOpen: true,
          anchorEl: event.currentTarget,
          snackbarMessage: warnings.join(' '),
          transmittableOrderLines,
        });
      } else {
        this.setState({
          snackbarOpen: true,
          snackbarMessage: warnings.join(' '),
          transmittableOrderLines,
        });
      }
    }
    if (errors.length > 0) {
      actions.addNotification({
        message: errors.join(' '),
        variant: 'warning',
      });
    }
  }

  onRejectButtonClick() {
    const { selectedOrderLines, actions, onSubmitSuccess } = this.props;
    new Promise((resolve, reject) => {
      this.setState({ extracting: true });
      actions.updateOrderLines({
        values: selectedOrderLines.filter(item => !item['Is Rejected']).map(item => ({
          ID: item.ID,
          orderID: item['Order ID'],
          'Is Rejected': true,
        })),
        resolve,
        reject,
      });
    }).then(
      (values) => {
        onSubmitSuccess(values);
        this.setState({ extracting: false });
      },
      () => {
        this.setState({ extracting: false });
      });
  }

  onUndoRejectedButtonClick() {
    const { selectedOrderLines, actions, onSubmitSuccess } = this.props;
    new Promise((resolve, reject) => {
      this.setState({ extracting: true });
      actions.updateOrderLines({
        values: selectedOrderLines.filter(item => item['Is Rejected']).map(item => ({
          ID: item.ID,
          orderID: item['Order ID'],
          'Is Rejected': false,
        })),
        resolve,
        reject,
      });
    }).then(
      (values) => {
        onSubmitSuccess(values);
        this.setState({ extracting: false });
      },
      () => {
        this.setState({ extracting: false });
      });
  }

  onSnackbarClose() {
    this.setState({
      snackbarOpen: false,
      snackbarMessage: '',
      withAttachment: false,
    });
  }

  onTransmitSubmitButtonClick() {
    this.onTransmit({
      orderLines: this.state.transmittableOrderLines,
      withAttachment: this.state.withAttachment,
      withClaim: this.state.withClaim,
    });
    this.setState({
      snackbarOpen: false,
      snackbarMessage: '',
      transmittableOrderLines: [],
    });
  }

  onTransmit({
    orderLines,
    withAttachment,
    withClaim,
  }) {
    const { actions } = this.props;
    new Promise((resolve, reject) => {
      this.setState({
        transmitting: true,
        transmitMenuOpen: false,
      });
      actions.transmitOrderLines({
        values: {
          'Order Lines': orderLines,
          'HCPC S Code': orderLines.reduce((accumulator, currentValue) => ({
            ...accumulator,
            [currentValue.ID]: currentValue['HCPC S Code']['HCPC S Code'],
          }), {}),
          withAttachment,
          withClaim,
        },
        resolve,
        reject,
      });
    }).then(
      () => {
        this.setState({ transmitting: false });
      },
      () => {
        this.setState({ transmitting: false });
      });
  }

  onTransmitMenuClose() {
    this.setState({ transmitMenuOpen: false });
  }

  render() {
    const {
      variant,
      count,
      selectedOrderLines,
      onSubmitSuccess,
      orders,
      account,
      appInfo,
      archive,
      business,
      classes,
    } = this.props;
    const {
      anchorEl,
      moveSection,
      moveMenuOpen,
      deleteMenuOpen,
      printMenuOpen,
      transmitting,
      moving,
      extracting,
      snackbarOpen,
      snackbarMessage,
      transmitMenuOpen,
      transmittableOrderLines,
    } = this.state;
    const open = Boolean(anchorEl);
    const selectedOrderLinesWithRejected = selectedOrderLines.filter(item => item['Is Rejected']).length > 0;
    const selectedOrderLinesWithNotRejected = selectedOrderLines.filter(item => !item['Is Rejected']).length > 0;
    return (
      <Toolbar
        className={classes.root}
        disableGutters
      >
        {(variant !== 'rejected'
        && appInfo['APP NAME'] !== 'demo'
        && !archive) &&
          <Fragment>
            <div className={classes.loaderContainer}>
              <IconButton
                className={classes.button}
                tooltipContent={!transmitting ? 'Transmit' : null}
                {...['create', 'copy'].indexOf(variant) !== -1 ? {
                  tooltipContent: 'Save Order to Enable Claim Transmition',
                } : {}}
                {...(appInfo['APP NAME'] !== 'alba'
                  && (!business['Company Subscription Payment']['Last Finished Payment']
                  || !business['Company Subscription Payment']['Last Finished Payment'].Status === 'payed')) ? {
                  tooltipContent: 'Paid version needed to Enable Claim Transmition',
                } : {}}
                disabled={transmitting
                  || ['create', 'copy'].indexOf(variant) !== -1
                  || (appInfo['APP NAME'] !== 'alba'
                    && (!business['Company Subscription Payment']['Last Finished Payment'] 
                      || !business['Company Subscription Payment']['Last Finished Payment'].Status === 'payed'))}
                onClick={this.onTransmitButtonClick}
              >
                <IconTransmit className={classes.iconTransmit} />
              </IconButton>
              {transmitting &&
                <Progress />
              }
            </div>
            <Menu
              id="transmit-menu"
              anchorEl={anchorEl}
              open={open && transmitMenuOpen}
              onClose={this.onTransmitMenuClose}
              placement="top-middle"
            >
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  if (snackbarMessage) {
                    this.setState({
                      snackbarOpen: true,
                      withAttachment: false,
                      transmitMenuOpen: false,
                    });
                  } else {
                    this.onTransmit({
                      orderLines: transmittableOrderLines,
                    });
                  }
                }}
              >
                Transmit
              </MenuItem>
              <MenuItem
                className={classes.menuItem}
                onClick={() => {
                  if (snackbarMessage) {
                    this.setState({
                      snackbarOpen: true,
                      withAttachment: true,
                      transmitMenuOpen: false,
                    });
                  } else {
                    this.onTransmit({
                      orderLines: transmittableOrderLines,
                      withAttachment: true,
                    });
                  }
                }}
              >
                Transmit with Attachment
              </MenuItem>
              {transmittableOrderLines.filter(item => item.Claim && item.Claim.length > 0) && (
                <MenuItem
                  className={classes.menuItem}
                  onClick={() => {
                    if (snackbarMessage) {
                      this.setState({
                        snackbarOpen: true,
                        withAttachment: true,
                        withClaim: false,
                        transmitMenuOpen: false,
                      });
                    } else {
                      this.onTransmit({
                        orderLines: transmittableOrderLines,
                        withAttachment: true,
                        withClaim: false,
                      });
                    }
                  }}
                >
                  Transmit Attachment Only
                </MenuItem>
              )}
            </Menu>
            <Snackbar
              open={snackbarOpen}
              variant="info"
              message={snackbarMessage}
              autoHideDuration={5000}
              resumeHideDuration={0}
              onClose={this.onSnackbarClose}
              action={(
                <Button
                  variant="raised"
                  color="primary"
                  onClick={this.onTransmitSubmitButtonClick}
                >
                  Submit
                </Button>
              )}
            />
          </Fragment>
        }
        {(['create', 'copy', 'rejected'].indexOf(variant) === -1 && !archive) &&
          <Fragment>
            <div className={classes.loaderContainer}>
              <IconButton
                className={classNames(classes.button, classes.buttonMove)}
                aria-label="Move"
                aria-owns={open && moveMenuOpen ? 'orders-move-menu' : null}
                aria-haspopup="true"
                onClick={this.onMoveButtonClick}
                tooltipContent="Move"
                disabled={extracting || moving}
              >
                <IconMove className={classes.iconMove} />
                <IconTriangleBottom className={classes.iconTriangleBottom} />
              </IconButton>
              {moving &&
                <Progress />
              }
            </div>
            <Menu
              id="orders-move-menu"
              anchorEl={anchorEl}
              open={open && moveMenuOpen}
              onClose={this.onMenuClose}
              onExited={this.onMenuExited}
              placement="top-middle"
              maxHeight={244}
              {...moveSection ? {
                list: false,
              } : {
                listHeader: (
                  <Typography variant="caption">
                    Move to section:
                  </Typography>
                ),
              }}
            >
              {moveSection ? (
                <OrdersActionsBarMoveForm
                  form="ordersActionsBarMoveForm"
                  moveSection={moveSection}
                  selectedOrderLines={selectedOrderLines}
                  onSubmitSuccess={(values) => {
                    this.onMenuClose();
                    onSubmitSuccess(values);
                  }}
                  orders={orders}
                />
              ) : (
                <Fragment>
                  {variant !== 'intake' &&
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => this.onMoveMenuItemClick('Intake')}
                    >
                      Intake
                    </MenuItem>
                  }
                  {variant !== 'delivery' &&
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => this.onMoveMenuItemClick('Delivery')}
                    >
                      Delivery
                    </MenuItem>
                  }
                  {variant !== 'billing' &&
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => this.onMoveMenuItemClick('Billing')}
                    >
                      Billing
                    </MenuItem>
                  }
                  {variant !== 'collection' &&
                    <MenuItem
                      className={classes.menuItem}
                      onClick={() => this.onMoveMenuItemClick('Collection')}
                    >
                      Collection
                    </MenuItem>
                  }
                  <MenuItem
                    className={classes.menuItem}
                    onClick={() => this.onMoveMenuItemClick('Paid')}
                  >
                    Paid
                  </MenuItem>
                </Fragment>
              )}
            </Menu>
          </Fragment>
        }
        {(((variant === 'rejected' || variant === 'combined') && selectedOrderLinesWithNotRejected) &&
          account.Roles.filter(item =>
            item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length > 0) &&
          <div className={classes.loaderContainer}>
            <IconButton
              className={classNames(classes.button)}
              onClick={this.onRejectButtonClick}
              tooltipContent="Move to Rejected"
              disabled={extracting || moving}
            >
              <div className={classes.iconRejected}>
                <IconArticle className={classes.iconArticle} />
                <IconCrossBold className={classes.iconCrossBold} />
                <IconUndo className={classes.iconRedo} />
              </div>
            </IconButton>
            {extracting &&
              <Progress />
            }
          </div>
        }
        {((variant === 'rejected' || variant === 'combined') && selectedOrderLinesWithRejected) &&
          <div className={classes.loaderContainer}>
            <IconButton
              className={classNames(classes.button)}
              onClick={this.onUndoRejectedButtonClick}
              tooltipContent="Undo Rejected"
              disabled={extracting || moving}
            >
              <div className={classes.iconRejected}>
                <IconArticle className={classes.iconArticle} />
                <IconCrossBold className={classes.iconCrossBold} />
                <IconUndo className={classes.iconUndo} />
              </div>
            </IconButton>
            {extracting &&
              <Progress />
            }
          </div>
        }
        {(variant !== 'create' && variant !== 'copy') &&
          <Fragment>
            <IconButton
              className={classes.button}
              tooltipContent={orders
                ? 'Print' : `Print ${count} ${formatSinglePlural({
                  quantity: count, single: 'item', plural: 'items',
                })}`}
              onClick={this.onPrintButtonClick}
              {...orders ? {
                'aria-label': 'Print',
                'aria-owns': open && deleteMenuOpen ? 'orders-print-menu' : null,
                'aria-haspopup': true,
              } : {}}
            >
              <IconPrint className={classes.iconPrint} />
            </IconButton>
            {orders &&
              <Menu
                id="orders-print-menu"
                anchorEl={anchorEl}
                open={open && printMenuOpen}
                onClose={this.onMenuClose}
                placement="top-middle"
                listHeader={(
                  <Typography variant="caption">
                    Print Order:
                  </Typography>
                )}
              >
                {
                  orders.reduce((accumulator, currentValue) => {
                    const orderLine = currentValue['Order Line']
                      .filter(item => selectedOrderLines
                        .findIndex(selectedOrderLine => selectedOrderLine.ID === item.ID) !== -1);
                    if (orderLine.length > 0) {
                      return [
                        ...accumulator,
                        {
                          ...currentValue,
                          'Order Line': orderLine,
                        },
                      ];
                    }
                    return accumulator;
                  }, []).map(order => (
                    <MenuItem
                      key={order.ID}
                      className={classes.menuItem}
                      onClick={() => this.onPrintMenuItemClick(order)}
                    >
                      {`${formatOrderNumber(order.ID)} (${order['Order Line'].map(orderLine => orderLine['HCPC S Code']['HCPC S Code']).join(', ')})`}
                    </MenuItem>
                  ))
                }
              </Menu>
            }
          </Fragment>
        }
        {(['create', 'edit', 'copy'].indexOf(variant) !== -1 && !archive) &&
          <Fragment>
            <IconButton
              className={classes.button}
              aria-label="Remove"
              aria-owns={open && deleteMenuOpen ? 'orders-actions-bar-remove-menu' : null}
              aria-haspopup="true"
              onClick={this.onRemoveButtonClick}
              tooltipContent={`Delete ${count} items`}
            >
              <IconTrashCan className={classes.iconTrashCan} />
            </IconButton>
            <Menu
              id="orders-actions-bar-remove-menu"
              anchorEl={anchorEl}
              open={open && deleteMenuOpen}
              onClose={this.onMenuClose}
              list={false}
              placement="top-middle"
            >
              <DeleteMenu
                text="Are you sure you want to delete selected items?"
                onCancel={this.onRemoveCancel}
                onSubmit={this.onRemoveSubmit}
              />
            </Menu>
          </Fragment>
        }
      </Toolbar>
    );
  }
}

OrdersActionsBarPrimaryControls.propTypes = {
  variant: PropTypes.oneOf([
    'create',
    'edit',
    'view',
    'copy',
    'intake',
    'delivery',
    'billing',
    'collection',
    'rejected',
    'combined',
  ]).isRequired,
  count: PropTypes.number.isRequired,
  selectedOrderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  onSubmitSuccess: PropTypes.func,
  onRemove: PropTypes.func,
  onPrint: PropTypes.func,
  orders: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })),
  actions: PropTypes.shape({
    transmitOrderLines: PropTypes.func.isRequired,
    updateOrderLines: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  appInfo: PropTypes.shape({
    'APP NAME': PropTypes.string,
  }).isRequired,
  archive: PropTypes.bool,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    buttonMove: PropTypes.string.isRequired,
    buttonSyncActive: PropTypes.string.isRequired,
    iconSync: PropTypes.string.isRequired,
    iconTransmit: PropTypes.string.isRequired,
    iconMove: PropTypes.string.isRequired,
    iconTriangleBottom: PropTypes.string.isRequired,
    iconPrint: PropTypes.string.isRequired,
    iconTrashCan: PropTypes.string.isRequired,
    iconNotRejected: PropTypes.string.isRequired,
    menuItem: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersActionsBarPrimaryControls.defaultProps = {
  onSubmitSuccess: () => {},
  onRemove: () => {},
  onPrint: () => {},
  orders: null,
  selectedOrderLines: [],
  archive: false,
};

const mapStateToProps = store => ({
  account: store.account,
  appInfo: store.appInfo,
  business: store.business.data,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    transmitOrderLines, addNotification, updateOrderLines,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(OrdersActionsBarPrimaryControls),
);



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersActionsBar/OrdersActionsBarPrimaryControls.js